import React, { useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  Marker,
  StandaloneSearchBox,
  Circle,
  useJsApiLoader,
} from "@react-google-maps/api";

import "./LocationPicker.css";

const libraries = ["places"];

const containerStyle = {
  width: "100%",
  height: "500px",
};

const defaultCenter = {
  lat: 10.0735,
  lng: 78.7794,
};

export default function LocationPicker({
  open,
  onClose,
  onSelect,
  initialLocation,
}) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  /* -------------------------------------------------------------------------- */
  /*                                  STATES                                    */
  /* -------------------------------------------------------------------------- */

  const [map, setMap] = useState(null);

  const searchRef = useRef(null);

  const [mapType, setMapType] = useState("roadmap");

  const [zoom, setZoom] = useState(16);

  const [loading, setLoading] = useState(false);

  const [radius, setRadius] = useState(5000);

  const [location, setLocation] = useState({
    address: "",
    city: "",
    district: "",
    state: "",
    pincode: "",
    country: "India",
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
  });

  const [marker, setMarker] = useState(defaultCenter);

  /* -------------------------------------------------------------------------- */
  /*                                INITIAL DATA                                */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    if (
      initialLocation &&
      typeof initialLocation.latitude === "number" &&
      typeof initialLocation.longitude === "number"
    ) {
      setMarker({
        lat: initialLocation.latitude,
        lng: initialLocation.longitude,
      });

      setLocation({
        address: initialLocation.address || "",
        city: initialLocation.city || "",
        district: initialLocation.district || "",
        state: initialLocation.state || "",
        pincode: initialLocation.pincode || "",
        country: initialLocation.country || "India",
        latitude: initialLocation.latitude,
        longitude: initialLocation.longitude,
      });
    } else {
      setMarker(defaultCenter);
    }
  }, [initialLocation]);

  if (!open) return null;

  /* -------------------------------------------------------------------------- */
  /*                            ADDRESS COMPONENTS                              */
  /* -------------------------------------------------------------------------- */

  const getComponent = (components, type) => {
    return (
      components.find((c) => c.types.includes(type))
        ?.long_name || ""
    );
  };

  /* -------------------------------------------------------------------------- */
  /*                             REVERSE GEOCODING                              */
  /* -------------------------------------------------------------------------- */

  const reverseGeocode = (lat, lng) => {
    if (!window.google) return;

    setLoading(true);

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      {
        location: {
          lat,
          lng,
        },
      },
      (results, status) => {
        setLoading(false);

        if (
          status === "OK" &&
          results &&
          results.length > 0
        ) {
          const result = results[0];

          const components =
            result.address_components;

          setLocation({
            address:
              result.formatted_address,

            city:
              getComponent(
                components,
                "locality"
              ) ||
              getComponent(
                components,
                "sublocality_level_1"
              ) ||
              getComponent(
                components,
                "postal_town"
              ) ||
              getComponent(
                components,
                "administrative_area_level_3"
              ),

            district:
              getComponent(
                components,
                "administrative_area_level_2"
              ),

            state:
              getComponent(
                components,
                "administrative_area_level_1"
              ),

            pincode:
              getComponent(
                components,
                "postal_code"
              ),

            country:
              getComponent(
                components,
                "country"
              ),

            latitude: lat,
            longitude: lng,
          });
        }
      }
    );
  };

  /* -------------------------------------------------------------------------- */
  /*                              MAP CLICK                                     */
  /* -------------------------------------------------------------------------- */

  const onMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarker({
      lat,
      lng,
    });

    reverseGeocode(lat, lng);
  };

  /* -------------------------------------------------------------------------- */
  /*                           MARKER DRAG                                      */
  /* -------------------------------------------------------------------------- */

  const onMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarker({
      lat,
      lng,
    });

    reverseGeocode(lat, lng);
  };

  /* -------------------------------------------------------------------------- */
  /*                            SEARCH PLACE                                    */
  /* -------------------------------------------------------------------------- */

  const searchPlace = () => {
    const places =
      searchRef.current.getPlaces();

    if (!places || places.length === 0)
      return;

    const place = places[0];

    if (!place.geometry) return;

    const lat =
      place.geometry.location.lat();

    const lng =
      place.geometry.location.lng();

    setMarker({
      lat,
      lng,
    });

    if (map) {
      map.panTo({
        lat,
        lng,
      });

      map.setZoom(18);
    }

    reverseGeocode(lat, lng);
  };

  /* -------------------------------------------------------------------------- */
  /*                          CURRENT LOCATION                                  */
  /* -------------------------------------------------------------------------- */

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat =
          position.coords.latitude;

        const lng =
          position.coords.longitude;

        setMarker({
          lat,
          lng,
        });

        if (map) {
          map.panTo({
            lat,
            lng,
          });

          map.setZoom(18);
        }

        reverseGeocode(lat, lng);
      },
      () => {
        alert(
          "Unable to fetch your current location."
        );
      }
    );
  };

  /* -------------------------------------------------------------------------- */
  /*                            MAP TYPE                                        */
  /* -------------------------------------------------------------------------- */

  const changeMapType = (type) => {
    setMapType(type);
  };

  /* -------------------------------------------------------------------------- */
  /*                             ZOOM CONTROLS                                  */
  /* -------------------------------------------------------------------------- */

  const zoomIn = () => {
    if (!map) return;

    const z = map.getZoom() + 1;

    map.setZoom(z);

    setZoom(z);
  };

  const zoomOut = () => {
    if (!map) return;

    const z = map.getZoom() - 1;

    map.setZoom(z);

    setZoom(z);
  };

  /* -------------------------------------------------------------------------- */
  /*                             FULLSCREEN                                     */
  /* -------------------------------------------------------------------------- */

  const openFullscreen = () => {
    if (!map) return;

    const div = map.getDiv();

    if (div.requestFullscreen) {
      div.requestFullscreen();
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                           COPY COORDINATES                                 */
  /* -------------------------------------------------------------------------- */

  const copyCoordinates = () => {
    navigator.clipboard.writeText(
      `${location.latitude}, ${location.longitude}`
    );

    alert("Coordinates copied.");
  };

  /* -------------------------------------------------------------------------- */
  /*                           RESET LOCATION                                   */
  /* -------------------------------------------------------------------------- */

  const resetLocation = () => {
    setMarker(defaultCenter);

    reverseGeocode(
      defaultCenter.lat,
      defaultCenter.lng
    );

    if (map) {
      map.panTo(defaultCenter);

      map.setZoom(15);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                          MAP OPTIONS                                       */
  /* -------------------------------------------------------------------------- */

  const mapOptions = {
    streetViewControl: false,

    fullscreenControl: false,

    mapTypeControl: false,

    clickableIcons: false,

    gestureHandling: "greedy",

    zoomControl: false,

    rotateControl: false,

    keyboardShortcuts: true,
  };

  /* -------------------------------------------------------------------------- */
  /*                         DELIVERY CIRCLE OPTIONS                            */
  /* -------------------------------------------------------------------------- */

  const circleOptions = {
    fillColor: "#2563eb",
    fillOpacity: 0.15,

    strokeColor: "#2563eb",
    strokeOpacity: 0.8,

    strokeWeight: 2,

    clickable: false,

    draggable: false,

    editable: false,

    visible: true,
  };

  // ===========================
  // JSX STARTS IN PART 2
  // ===========================

    return (
    <div className="vendor-modal-overlay">

      <div className="location-picker-modal">

        {/* ================= HEADER ================= */}

        <div className="location-picker-header">
          <h3>Select Business Location</h3>

          <button
            className="close-btn"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* ================= SEARCH ================= */}

        {isLoaded && (
          <div className="search-wrapper">
            <StandaloneSearchBox
              onLoad={(ref) => (searchRef.current = ref)}
              onPlacesChanged={searchPlace}
            >
              <input
                className="location-search"
                placeholder="Search business address..."
              />
            </StandaloneSearchBox>
          </div>
        )}

        {/* ================= TOOLBAR ================= */}

        <div className="map-toolbar">

          <button
            onClick={useCurrentLocation}
            title="Current Location"
          >
            📍 Current
          </button>

          <button
            onClick={() =>
              changeMapType("roadmap")
            }
            className={
              mapType === "roadmap"
                ? "active"
                : ""
            }
          >
            🗺 Map
          </button>

          <button
            onClick={() =>
              changeMapType("satellite")
            }
            className={
              mapType === "satellite"
                ? "active"
                : ""
            }
          >
            🛰 Satellite
          </button>

          <button
            onClick={resetLocation}
          >
            Reset
          </button>

        </div>

        {/* ================= MAP ================= */}

        <div className="map-wrapper">

          {loading && (
            <div className="map-loading">
              Fetching Address...
            </div>
          )}

          {isLoaded && (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={marker}
              zoom={zoom}
              onLoad={setMap}
              onClick={onMapClick}
              mapTypeId={mapType}
              options={mapOptions}
            >

              {/* Delivery Radius */}

              <Circle
                center={marker}
                radius={radius}
                options={circleOptions}
              />

              {/* Marker */}

              <Marker
                position={marker}
                draggable
                animation={
                  window.google.maps.Animation.DROP
                }
                onDragEnd={onMarkerDragEnd}
              />

            </GoogleMap>
          )}

          {/* Floating Zoom */}

          <div className="zoom-controls">

            <button onClick={zoomIn}>
              +
            </button>

            <button onClick={zoomOut}>
              −
            </button>

          </div>

          {/* Floating Fullscreen */}

          <button
            className="fullscreen-btn"
            onClick={openFullscreen}
          >
            ⛶
          </button>

        </div>

        {/* ================= DELIVERY RADIUS ================= */}

        <div className="radius-section">

          <label>
            Delivery Radius :
            <strong>
              {" "}
              {radius / 1000} km
            </strong>
          </label>

          <input
            type="range"
            min={1000}
            max={25000}
            step={1000}
            value={radius}
            onChange={(e) =>
              setRadius(
                Number(e.target.value)
              )
            }
          />

        </div>

        {/* ================= ADDRESS ================= */}

        <div className="address-card">

          <h4>
            Selected Address
          </h4>

          <textarea
            value={location.address}
            readOnly
            rows={4}
          />

          <div className="address-grid">

            <div>
              <label>City</label>
              <span>
                {location.city || "-"}
              </span>
            </div>

            <div>
              <label>District</label>
              <span>
                {location.district || "-"}
              </span>
            </div>

            <div>
              <label>State</label>
              <span>
                {location.state || "-"}
              </span>
            </div>

            <div>
              <label>Pincode</label>
              <span>
                {location.pincode || "-"}
              </span>
            </div>

          </div>

        </div>

        {/* ================= COORDINATES ================= */}

        <div className="coordinates-card">

          <div>

            <label>Latitude</label>

            <span>
              {location.latitude}
            </span>

          </div>

          <div>

            <label>Longitude</label>

            <span>
              {location.longitude}
            </span>

          </div>

          <button
            className="copy-btn"
            onClick={copyCoordinates}
          >
            Copy Coordinates
          </button>

        </div>

        {/* ================= FOOTER ================= */}

        <div className="location-footer">

          <button
            className="cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            className="admin-primary-btn"
            onClick={() => {

              onSelect({
                ...location,

                geo: {
                  type: "Point",

                  coordinates: [
                    location.longitude,
                    location.latitude,
                  ],
                },
              });

              onClose();

            }}
          >
            Confirm Location
          </button>

        </div>

      </div>

    </div>
  );

}